import React, { Suspense } from 'react'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import { PlacesAppProvider } from './components/PlacesApp/PlacesAppContext'
import * as serviceWorker from './serviceWorker'
import './locales'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

const treeSentryDefaults = {
  ignoreErrors: [
    /^.*-extension:\/\/.*$/, // Browser extensions (chrome-extension, moz-extension, etc.).
    /^.*AbortError: The transaction was aborted.*$/, // We think this happens when you cancel loading a page.
    /^.*AbortError: Version change transaction was aborted.*$/, // Primarily Facebook browser on mobile.
    /^.*Blocked a frame with origin.*$/, // We don't use frames for any of our content.
    /^.*Can't find variable.*$/, // _satellite and SymBrowser_ModifyWindowOpenWithTarget are referenced by the Apple Mail client.
    /^.*Connection is closing.*$/, // Happens when page is hibernated.
    /^.*Connection on cached page closed.*$/, // Happens when page is hibernated.
    /^.*connection was closed.*$/, // Happens when page is hibernated.
    /^.*ChunkLoadError.*$/, // Figure out HOW this happens, and fix it.
    /^.*Database deleted.*$/, // Known caching error state.
    /^.*database is a higher version.*$/, // Caching error state.
    /^.*database using a lower version.*$/, // Caching error state.
    /^.*disk is full.*$/, // Caching error state.
    /^.*Error creating or migrating .* in database.*$/, // IndexedDB error in Safari.
    /^.*Error looking up record in object store.*$/, // Mac-based database errors.
    /^.*Error:.*Indexed Database server.*$/, // Various IndexedDB errors.
    /^.*Extension context invalidated.*$/, // Chrome Extension error that is not caught by the one above.
    /^.*Failed to execute 'replaceState' on 'History'.*$/, // Happens when users are utilizing google translate, instead of lang picker.
    /^.*flatMap.*$/, // issue in older browsers https://stackoverflow.com/questions/55530690/javascript-flatmap-method-over-array-flatmap-is-not-a-function
    /^.*Internal error retrieving bucket.*$/, // Split.io intermittent error.
    /^.*is not defined.*$/, // utag/apstagLOADED happens when analytics is blocked.
    /^.*Java object is gone.*$/, // Android WebView/Instagram browser issue where Java objects are garbage collected.
    /^.*Loading CSS chunk.*$/, // This should be caught by the css denyUrls entry, but isn't.
    /^.*Munchkin.*$/, // Munchkin marketing tracker appears to have problems.
    /^.*Network Error.*$/, // This is an expected error for when an Axios request cannot contact one of our servers.
    /^.*NetworkError when attempting to fetch resource.*$/, // Network connectivity issues, ad blockers, or browser-level fetch failures.
    /^.*No Session Id.*$/, // Expected error from zion-user.
    /^.*no user signed in.*$/, // Expected error from zion-preferences.
    /^.*Non-Error promise rejection captured.*$/, // Happens on app 301 redirects.
    /^.*One of the specified object stores was not found.*$/, // IndexedDB broken state error.
    /^.*QuotaExceededError.*$/, // Android browser local storage errors related to a full disk.
    /^.*redefine .*property.*$/, // AdBlock/userAgent/etc.
    /^.*Request aborted.*$/, // Expected Axios behavior for aborted requests.
    /^.*ResizeObserver loop limit exceeded.*$/, // Benign browser bug in Chrome / Edge on Windows.
    /^.*SecurityError: The operation is insecure.*$/, // Firefox SSL error that happens if the App is SSL and accesses something that is not SSL.
    /^.*status.*401.*$/, // Happens when a user session times out.
    /^.*status.*403.*$/, // Insufficient access.
    /^.*status.*404.*$/, // We actually care about why people get 404's, just not quite yet.
    /^.*status.*410.*$/, // 410 Gone data responses are acceptable.
    /^.*status.*422.*$/, // 422 Throttled responses are acceptable.
    /^.*The operation failed for reasons unrelated to the database itself.*$/, // Firefox error.
    /^.*The requested version.*$/, // Database caching sync error.
    /^.*The user denied permission to access the database.*$/, // User at fault.
    /^.*Transaction timed out due to inactivity.*$/, // Timeouts usually caused by losing connection or switching networks.
    /^.*Unable to open database.*$/, // IndexedDB error on Apple devices.
    /^.*DataError: Failed to read large IndexedDB value.*$/, // More IndexedDB errors from Opera browsers
    /^.*window\.onunload is not a function.*$/, // Facebook browser issue.
    /^.*is not a child of this node.*$/, // There are a lot of handled errors coming from upstreams that we are honestly sick of losing quota to if no one is going to fix them.
    /^.*Attempt to get a record from database without an in-progress transaction.*$/, // Adobe experience manager errors, noticed on mobile apps
    /^.*Network Error.*(dynatrace|dtAWF).*$/, // Only ignore Dynatrace network errors
    /^.*AxiosError.*dtAWF.*$/, // Dynatrace AxiosError cases
    /^.*AxiosError.*dynatrace.*$/, // Dynatrace AxiosError cases (alternative pattern)
    /^.*AxiosError.*ruxitagent.*$/, // Dynatrace RUM agent AxiosError cases
    /^.*AxiosError.*jstag.*$/, // Dynatrace jstag AxiosError cases
  ],
  denyUrls: [
    /edge\.fscdn\.org\/assets\/static\/css\//, // These CSS chunk load errors seem to happen when you navigate away before the page is fully loaded.
    /js-cdn\.dynatrace\.com\/jstag\//, // Block Dynatrace-originated errors
    /js-cdn\.dynatrace\.com/, // Block all Dynatrace CDN errors
    /ruxitagent/, // Block Dynatrace RUM agent errors
    /dtAWF/, // Block dtAWF function errors
  ],
  // Only care about code we nominally own and host. This should be cleaner than one-offing tons of urls to ignore
  allowUrls: [
    /edge\.fscdn\.org/, // Primary location. This is our Akamai CDN where build assets (read: code) are deployed to.
    /familysearchcdn\.org/,
    /https?:\/\/((www|beta|integration)\.)?familysearch\.org/, // googleads URL-encodes our pages and passes them around in GET parameters, so we need to be a bit more precise than otherwise.
  ],
}

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      ...treeSentryDefaults,
      dsn: sentryDSN,
      ignoreErrors: [
        "Must wait for 'idle' event on map before calling markersNearAnyOtherMarker",
        /.*RequestAnimationFrame.*/,
        /.*status.*401.* /,
        /.*-extension:\/\/.*/, // Browser extensions (chrome-extension, moz-extension, etc.)
        /.*Blocked a frame with origin.*/,
        /.*ChunkLoadError.*/,
        /.*Extension context invalidated.*/, // Chrome Extension error that is not caught by the first
        /.*no user signed in.*/, // Expected error from zion-preferences
        /.*QuotaExceededError.*/, // Non-critical localstorage errors
        /.*StorageQuotaExceeded.*/, // Non-critical localstorage errors
        /.*redefine .*property.*/, // AdBlock/userAgent/etc.
        /.*ResizeObserver loop limit exceeded*/, // Benign browser bug in Chrome / Edge on Windows
        /.*status.*401.*/, // Happens when a user session times out
        /.*status.*404.*/, // We actually care about why people get 404's, just not quite yet
        /.*status.*410.*/, // 410 Gone data responses are acceptable
        /.*Non-Error promise rejection captured with keys:.*/, // Error that appears but doesn't seem to cause issues with site
        /.*utag is not defined.*/, // Happens when analytics is blocked
        /.*flatMap is not a function.*/, // Common issue in ProgressOverlayContext, not critical error
      ],
      sampleRate: 0.75,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  return (
    <PlacesAppProvider>
      <Router basename={base}>
        <Root skipLinkId="resultsWrapper" analytics footer={{ hidden: true }} helpChatbot={false}>
          <AppLayout fullWidth>
            <App />
          </AppLayout>
        </Root>
      </Router>
    </PlacesAppProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
